import { useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead, Meter, StatusBadge } from "../components/ui";
import { StageLightbox, stageState, TONE_CLASS } from "../components/StageDetail";
import { StageSubmissionsPanel } from "../components/StageSubmissions";
import { useSession } from "../lib/auth";
import { Icon } from "../components/Icons";
import { actions, stageProgress, money, useRateCard } from "../lib/store";
import { computeEstimate, defaultConfig, PHASE_OUTLINE } from "../lib/rates";
import { useCan } from "../lib/auth";
import { fmtDate } from "../lib/format";

const PHASE_COLOR: Record<string, string> = { sd: "var(--sky)", dd: "var(--sage)", cd: "var(--accent)", tender: "var(--gold)", ca: "var(--ink-3)" };
const STAGE_PHASE: Record<string, string> = { sd: "Conceptual", dd: "Design Development", cd: "IFT" };

export default function Stages() {
  const p = useCurrent();
  const [openStage, setOpenStage] = useState<number | null>(null);
  const canEdit = useCan("stages");
  const session = useSession();
  const rc = useRateCard();
  if (!p) return null;
  const canAdvance = canEdit && p.stages.some((s) => s.status === "active") && p.stages[p.stages.length - 1].status !== "active";
  const est = computeEstimate(p.estimate ?? defaultConfig(p.budget, p.currency), rc);
  const ccy = (p.estimate ?? { currency: p.currency }).currency;
  const deliverables = p.deliverables ?? [];

  return (
    <Page max={860}>
      <PageHead kicker={`${p.name} · Progress`} title="Project stages">
        {canAdvance && <button className="btn accent sm" onClick={() => actions.advanceStage(p.id)}><Icon.arrow width={15} />Advance stage</button>}
      </PageHead>

      <div className="card pad reveal" style={{ marginBottom: 26 }}>
        <div className="row between" style={{ marginBottom: 12 }}>
          <span className="kicker">Overall completion</span>
          <span className="serif" style={{ fontSize: 22 }}>{stageProgress(p)}%</span>
        </div>
        <Meter value={stageProgress(p)} />
      </div>

      <div style={{ position: "relative", paddingLeft: 8 }}>
        {p.stages.map((s, i) => {
          const active = s.status === "active";
          const done = s.status === "done";
          return (
            <div key={s.key} className="reveal" style={{ display: "grid", gridTemplateColumns: "40px 1fr", gap: 18, position: "relative", paddingBottom: i === p.stages.length - 1 ? 0 : 28 }}>
              {/* rail */}
              {i < p.stages.length - 1 && <div style={{ position: "absolute", left: 19, top: 34, bottom: 0, width: 2, background: done ? "var(--accent)" : "var(--line-2)" }} />}
              {/* node */}
              <div style={{ width: 40, height: 40, borderRadius: 40, display: "grid", placeItems: "center", flexShrink: 0, zIndex: 1,
                background: done ? "var(--accent)" : active ? "var(--ink)" : "var(--card)",
                color: done || active ? "#fff" : "var(--ink-3)",
                border: active ? "2px solid var(--accent)" : "1px solid var(--line-2)",
                boxShadow: active ? "0 0 0 5px var(--accent-soft)" : "none" }}>
                {done ? <Icon.check width={18} /> : <span className="mono" style={{ fontSize: 13 }}>{i + 1}</span>}
              </div>
              {/* body */}
              <button className="card pad stage-row" onClick={() => setOpenStage(i)} style={{ marginTop: -2, textAlign: "left", cursor: "pointer", width: "100%", display: "block", borderColor: active ? "color-mix(in srgb, var(--accent) 40%, var(--line))" : "var(--line)", background: active ? "color-mix(in srgb, var(--accent) 5%, var(--card))" : "var(--card)" }}>
                <div className="row between wrap" style={{ gap: 10 }}>
                  <div className="row wrap" style={{ gap: 10 }}>
                    <h3 className="serif" style={{ fontSize: 21 }}>{s.label}</h3>
                    {active && <span className="badge accent"><span className="dot" />You are here</span>}
                  </div>
                  <span className={TONE_CLASS[stageState(p, s, "studio").tone]} style={{ fontSize: 9.5 }}>
                    {stageState(p, s, "studio").tone === "wait" && <span className="dot" />}{stageState(p, s, "studio").label}
                  </span>
                </div>
                <div className="muted" style={{ marginTop: 8, fontSize: 14.5 }}>{s.blurb}</div>
                <div className="row between wrap" style={{ gap: 12, marginTop: 12 }}>
                  <div className="row dim wrap" style={{ gap: 16, fontSize: 12.5 }}>
                    {s.startedOn && <span className="row" style={{ gap: 5 }}><Icon.clock width={13} />Started {fmtDate(s.startedOn)}</span>}
                    {s.completedOn && <span className="row" style={{ gap: 5 }}><Icon.check width={13} />Completed {fmtDate(s.completedOn)}</span>}
                  </div>
                  <span className="stage-cta row" style={{ gap: 5, fontSize: 11.5, color: "var(--accent-ink)", fontWeight: 600 }}>What happens here<Icon.arrowUpRight width={12} /></span>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <StageLightbox stage={openStage === null ? null : p.stages[openStage]} index={openStage ?? 0} p={p} you="studio" onClose={() => setOpenStage(null)} />
      <style>{`
        .stage-row { transition: transform .28s var(--ease-out), box-shadow .28s var(--ease-out), border-color .2s ease; }
        .stage-row:hover { transform: translateY(-2px); box-shadow: var(--shadow-lg); }
        .stage-row:active { transform: translateY(0) scale(.996); }
        .stage-row .stage-cta { opacity: .5; transition: opacity .25s ease, gap .25s ease; }
        .stage-row:hover .stage-cta { opacity: 1; gap: 9px; }
        @media (prefers-reduced-motion: reduce){ .stage-row, .stage-row .stage-cta { transition: none } .stage-row:hover { transform: none } }
      `}</style>

      {/* formal stage submissions — the packaged close of each stage */}
      <StageSubmissionsPanel p={p} canEdit={canEdit} by={session?.name ?? "Studio"} />

      {/* full design-process breakdown */}
      <div className="row between reveal" style={{ margin: "40px 0 8px" }}>
        <div><div className="kicker">The design process</div><h3 className="serif" style={{ fontSize: 22, marginTop: 4 }}>Work stages in full</h3></div>
        <span className="dim" style={{ fontSize: 13 }}>{money(est.total, ccy)} fee</span>
      </div>
      <div className="card pad reveal">
        <div style={{ display: "flex", height: 10, borderRadius: 8, overflow: "hidden", background: "var(--line)", marginBottom: 18 }}>
          {est.phases.map((ph) => <div key={ph.key} title={`${ph.label} · ${ph.fixed ? "fixed fee" : `${ph.pctLabel}%`}`} style={{ flex: Math.max(0.001, ph.pct), background: PHASE_COLOR[ph.key] ?? "var(--ink-3)" }} />)}
        </div>
        <div className="col" style={{ gap: 12 }}>
          {est.phases.map((ph, i) => {
            const dv = deliverables.filter((d) => d.phase === STAGE_PHASE[ph.key]);
            return (
              <div key={ph.key} className="panel" style={{ padding: 16, borderLeft: `3px solid ${PHASE_COLOR[ph.key] ?? "var(--ink-3)"}` }}>
                <div className="row between wrap" style={{ gap: 10 }}>
                  <div className="row" style={{ gap: 12 }}>
                    <span className="serif" style={{ fontSize: 20, color: "var(--ink-3)", width: 26 }}>{String(i + 1).padStart(2, "0")}</span>
                    <div><div className="row" style={{ gap: 8 }}><span style={{ fontWeight: 600, fontSize: 15 }}>{ph.label}</span>{ph.construction && <span className="badge accent" style={{ fontSize: 9 }}>during construction</span>}</div><div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>{ph.fixed ? "fixed fee" : `${ph.pctLabel}% of fee`}</div></div>
                  </div>
                  <div className="serif" style={{ fontSize: 18 }}>{money(ph.amount, ccy)}</div>
                </div>
                {PHASE_OUTLINE[ph.key] && <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginTop: 12 }}>{PHASE_OUTLINE[ph.key]}</p>}
                {dv.length > 0 && <div className="row wrap" style={{ gap: 6, marginTop: 10 }}>{dv.map((d) => <span key={d.id} className="badge" style={{ fontSize: 10.5 }}><span className="mono">{d.code}</span> {d.title}</span>)}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </Page>
  );
}
